import { CEFR_ORDINAL } from "./cefrScale";

// Convert numeric level (1-6) to CEFR string (A1-C2)
const NUMERIC_TO_CEFR = {
  1: "A1",
  2: "A2",
  3: "B1",
  4: "B2",
  5: "C1",
  6: "C2",
};

/**
 * Read the user's CEFR level (1-6) for a given language code from the
 * userDetails object. Returns undefined when the level isn't set.
 *
 * userDetails stores per-language CEFR under `<lang>_cefr_level` keys
 * (e.g. `de_cefr_level`, `en_cefr_level`).
 */
export function getUserCefrLevel(userDetails, languageCode) {
  if (!userDetails || !languageCode) return undefined;
  return userDetails[languageCode + "_cefr_level"];
}

/**
 * The value the CEFR field should hold for a language: the user's stored level
 * for *that* language, as a CEFR_LEVELS value (the strings "1".."6"), or "" when
 * they have no level for it yet.
 *
 * Levels are stored per language, so this has to be re-read whenever the chosen
 * language changes -- otherwise the field keeps showing the previous language's
 * level and saves it under the new one.
 */
export function cefrLevelFieldValue(userDetails, languageCode) {
  const level = getUserCefrLevel(userDetails, languageCode);
  return level === undefined || level === null ? "" : String(level);
}

/**
 * Decide whether the language-choice modal is worth showing for an article
 * the user is about to read.
 *
 * Cross-language: always show — translate-and-adapt is real work regardless
 * of the source CEFR.
 *
 * Same-language: only show if the article is strictly harder than the user's
 * CEFR. If the article is already at or below their level, simplifying it
 * down doesn't help (and the backend rejects it). When we don't have a
 * solid level for either side, default to showing the modal — better to ask
 * than to silently skip an offer.
 */
export function shouldShowLanguageChoice(
  articleLanguage,
  articleCefrLevel,
  userDetails,
) {
  if (!userDetails) return true;
  if (articleLanguage !== userDetails.learned_language) return true;

  const userNumeric = getUserCefrLevel(userDetails, articleLanguage);
  const articleOrd = CEFR_ORDINAL[articleCefrLevel];
  if (!articleOrd || userNumeric == null) return true;
  return articleOrd > userNumeric;
}

/**
 * Convert numeric CEFR level to string.
 * User settings store levels as 1-6, API expects "A1"-"C2".
 *
 * @param {number|string} numericLevel - Level as number (1-6)
 * @returns {string} CEFR level string (A1-C2), defaults to B1
 */
export function numericToCefr(numericLevel) {
  return NUMERIC_TO_CEFR[numericLevel] || "B1";
}
